/**
 * Bloque fijado: al scrollear, las frases se escriben letra a letra, las piezas salen del
 * centro a su sitio y el fondo se transforma de un tono oscuro a otro (ajustable en el editor).
 *
 * Robustez (por qué está así):
 * - NO depende de eventos de scroll: un bucle de requestAnimationFrame repinta en CADA
 *   fotograma, sin condición. Los eventos de scroll llegaban a ratos (según navegador/
 *   extensiones) y la animación se quedaba congelada o "ya escrita".
 * - Repinta siempre, no solo si scrollY/tamaño de ventana cambian: hubo una versión que
 *   solo repintaba si esa "clave" cambiaba, y se veía bien tras mover/redimensionar la
 *   ventana (dispara el repintado) pero se quedaba congelada en el color/posición inicial
 *   tras un simple refresco. La causa: imágenes o la tipografía web cargando DESPUÉS del
 *   primer pintado desplazan la sección (cambia su rect.top) sin que scrollY cambie, así
 *   que esa "clave" nunca se actualizaba y el pintado quedaba obsoleto para siempre. Repintar
 *   sin condición es barato (unas pocas letras y piezas) y elimina la clase entera de bug.
 * - prepararFrases se llama DENTRO de cada pintado, no una sola vez. Horizon re-renderiza
 *   secciones; si las letras se partieran solo al cargar, un re-render las borraría.
 * - La clase .kotashi-pin-anim (que oculta el estado inicial) se pone en el primer pintado
 *   del bucle: si el JS no corre, todo se ve fijo y legible. Nunca una página en blanco.
 */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /** '#1B4B4A' → [190,155,111] */
  const hexRgb = (hex) => {
    const h = (hex || '').trim().replace('#', '');
    if (h.length !== 6) return null;
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  };

  /** Mezcla dos colores rgb con factor t (0..1). */
  const mezclar = (a, b, t) =>
    `rgb(${a.map((v, i) => Math.round(v + (b[i] - v) * t)).join(', ')})`;

  const tramo = (progreso, aparece, duracion) => {
    const x = (progreso - aparece) / duracion;
    return Math.max(0, Math.min(1, x));
  };

  /** Parte cada frase en <span> por letra. Idempotente: si ya está partida, no hace nada. */
  const prepararFrases = (seccion) => {
    for (const frase of seccion.querySelectorAll('.kotashi-pin__frase')) {
      if (frase.querySelector('.kotashi-pin__letra')) continue; // ya partida
      const texto = frase.textContent;
      frase.textContent = '';
      for (const caracter of texto) {
        const span = document.createElement('span');
        span.className = 'kotashi-pin__letra';
        // Espacio "duro" para que no se colapse y la palabra reserve su hueco.
        span.textContent = caracter === ' ' ? ' ' : caracter;
        frase.appendChild(span);
      }
    }
  };

  const pintar = () => {
    // Se re-buscan las secciones en cada pintado: si Horizon re-renderiza y reemplaza el
    // nodo, la referencia vieja quedaría muerta y la animación, congelada.
    for (const seccion of document.querySelectorAll('[data-kotashi-pin]')) {
      prepararFrases(seccion);

      const frases = seccion.querySelectorAll('.kotashi-pin__frase');
      const piezas = seccion.querySelectorAll('[data-pin-pieza]');
      const fijo = seccion.querySelector('.kotashi-pin__fijo');
      const rect = seccion.getBoundingClientRect();
      const recorrido = rect.height - window.innerHeight;
      const progreso = recorrido > 0 ? Math.max(0, Math.min(1, -rect.top / recorrido)) : 0;

      // Fondo: se transforma del primer color al segundo a lo largo del scroll.
      // En móvil NO: el CSS deja un solo color fijo (ver kotashi-pin.liquid) para que no haya
      // dos tonos ni transición que puedan "cortarse" en pantallas pequeñas. Aquí simplemente
      // no se toca ese fondo desde JS cuando el ancho es de móvil; las letras y las piezas
      // siguen animándose exactamente igual más abajo.
      const esMovil = window.matchMedia('(max-width: 749px)').matches;
      if (fijo && !esMovil) {
        const cs = getComputedStyle(seccion);
        const desde = hexRgb(cs.getPropertyValue('--kpin-desde'));
        const hasta = hexRgb(cs.getPropertyValue('--kpin-hasta'));
        if (desde && hasta) fijo.style.background = mezclar(desde, hasta, progreso);
      }

      // Texto: las frases se escriben en el primer 75% del recorrido, una tras otra.
      const hueco = 0.75 / Math.max(frases.length, 1);
      frases.forEach((frase, i) => {
        const t = tramo(progreso, i * hueco, hueco);
        const letras = frase.querySelectorAll('.kotashi-pin__letra');
        const escritas = Math.round(t * letras.length);
        letras.forEach((letra, j) => {
          letra.style.visibility = j < escritas ? 'visible' : 'hidden';
        });
      });

      // Piezas: aparecen en el centro y viajan a su posición final.
      for (const pieza of piezas) {
        const aparece = Number(pieza.dataset.aparece || 60) / 100;
        const destinoX = Number(pieza.dataset.x || 50);
        const t = tramo(progreso, aparece, 0.35);
        const suave = 1 - Math.pow(1 - t, 3);
        pieza.style.opacity = String(Math.min(1, t * 3));
        pieza.style.left = `${50 + (destinoX - 50) * suave}%`;
        const voltear = getComputedStyle(pieza).getPropertyValue('--kp-voltear').trim() || '1';
        pieza.style.transform = `translateY(${(1 - suave) * 40}px) scale(${0.6 + suave * 0.4}) scaleX(${voltear})`;
      }
    }
  };

  const bucle = () => {
    document.documentElement.classList.add('kotashi-pin-anim');
    pintar();
    requestAnimationFrame(bucle);
  };
  requestAnimationFrame(bucle);
})();
